import React from 'react'

import { Section, SectionHeading, Button } from '../../../../components/ui'
import { bookingPath, defaultWashSlug, enterBooking } from '../../../../lib/booking-entry'
import { readLowestWashPrice } from '../../../../lib/page-prices'
import { whatsappChatProps } from '../../../../components/whatsapp-chat'
import { goodToKnowFor } from '../../../../lib/good-to-know'

import './how-it-works.css'

// SLOT 7.2 - HOW IT WORKS. Design direction §7.2. Rebuilt 4 Oct.
//
// =====================================================================
// WHY THE FIRST BUILD WAS NOT ENOUGH
// =====================================================================
//
// The first version of this section was three numbered steps: book, a washer
// accepts, we come to the car. Every word of it was true and sourced. Two
// things were wrong with it anyway.
//
//   1. IT WAS THE SHAPE OF A SOFTWARE PRODUCT. Three numbered columns saying
//      "book / we match you / we arrive" describes a food delivery app, a
//      dog walker and a plumber marketplace equally well. Nothing in it
//      could only have been written by someone who does this work. The thing
//      the customer is buying is a trade, and the only way words carry a
//      trade is detail an outsider would not know to invent: what is in the
//      van, how the paint is actually washed, which four hour windows exist.
//      "Premium" is banned by name in DESIGN.md §3 precisely because it is
//      what gets written when the detail is missing. "Professional" was on
//      that list too until Robin ruled it allowed on 5 Oct (§3.1); the
//      reasoning here does not depend on the ban, because an adjective of
//      either kind would still be standing in for the detail. This file
//      spends its words on the detail instead.
//
//   2. IT ANSWERED ONE OBJECTION AND LEFT FIVE STANDING. 88.5% of people who
//      open the booking form never pay. A sequence of three steps tells
//      someone what the company does; it does not tell them whether they
//      have to be in, where the car has to be, what happens if it rains, or
//      what it costs to move the booking. A nervous customer does not think
//      in steps, they think in questions, and the honest answer is that this
//      section needs both: the sequence, because the silence between booking
//      and a van outside is the gap nobody explains, and then the questions,
//      because they are what is actually stopping the booking.
//
// So the section is now two blocks in one band: THREE moments down the left,
// and the five questions down the right. Eight answers where there were
// three.
//
// =====================================================================
// THREE MOMENTS, AND WHY THE ACCEPTANCE STEP IS GONE (5 Oct)
// =====================================================================
//
// This section carried a fourth step between booking and the day, and it was
// the worst paragraph on the site. It read: "Nothing is charged while we find
// a washer. The job goes to washers who cover your area, and your card is
// charged the moment one takes it. No message tells you that has happened. If
// nobody can take the slot, nothing is charged at all and we come back to you
// for another day."
//
// Every clause of it was true and sourced, and it still had to go. It
// narrated our dispatch mechanics to someone who had asked nothing about
// them; it drew attention to a silence the reader would never have noticed
// and made the absence feel like a gap; its heading ("while we find a
// washer") described the booking as an unresolved search; and it ENDED on
// the failure case, which is the position a reader remembers. At current
// volume essentially every booking is covered, so a paragraph built around
// the exception described the rare case as if it were the shape of the
// service. Robin, 5 Oct: "we dont want anyone thinking their booking is
// shakey.. it's a massive turnoff", and of this passage, "this might be too
// much information sharing tbh?"
//
// The money fact it carried is real and is kept, in step 1, where the
// customer is actually looking at a card field: nothing is charged when you
// book, and Stripe holds the card. The exception path is a human one and is
// not this section's business: if a booking cannot be covered, support
// contacts the customer (OrdersEvents#on_cancel(:admin) is the only path
// that messages them, and there is no cron that gives up on a slot).
//
// WHAT IS STILL NOT HERE, AND CANNOT BE. No time between booking and the
// day. There is no accept-latency metric in this codebase: no threshold, no
// flag, no scope, no cron, no admin column. `orders.sent_delayed_accepting_mail`
// (db/schema.rb) is a 2017 column nothing reads or writes, and
// Chatwoot::ContextNote's 'order_delayed_accepting' => 'Still finding an
// operator' is a label with no sender behind it. The two medians that exist
// are in research notes outside this repo, they disagree with each other
// (100 minutes on 30 Sep, 136 minutes on 4 Oct) and neither carries a query,
// an n or a date range. The development database has 365 seeded orders and
// zero rows in agents_orders_histories, so nothing is derivable here either.
// A time on this page is a promise an operator has to keep, so there is no
// time on this page. What a worried person gets instead is a way to ask: the
// WhatsApp link at the foot of the answers.
//
// LIKEWISE NO DURATION FOR THE WASH ITSELF. §7.3 and the hero both record
// that "About 45 minutes" was offered by the direction on condition Barry
// stood behind it, that nobody has asked him, and that `services` has no
// duration column. Nothing has changed, so nothing is stated.
//
// =====================================================================
// EVERY CLAIM, AND WHERE IT COMES FROM
// =====================================================================
//
// STEP 1
//   "the postcode and the         client/gwmc/components/book-postcode and
//   street the car is on"         components/book-address: two plain text
//                                 boxes, "Postcode" and "Car location"
//                                 ("House number and street, e.g. 10 Elm
//                                 Grove"). THIS LINE USED TO SAY "drop the
//                                 pin on the car", quoting the old FAQ, and
//                                 it instructed the customer to do something
//                                 the product cannot do: the Maps script,
//                                 Places, the geocoder, the pin and the
//                                 coordinates were all removed on 2026-10-04
//                                 (see the file comment in
//                                 components/book-address/index.js). Fixed
//                                 5 Oct.
//   the four windows              client/gwmc/components/time-slots/index.js
//                                 SLOTS: 'Anytime' 8am-6pm (pre-selected),
//                                 'Morning' 8am-12pm, 'Midday' 10am-2pm,
//                                 'Afternoon' 2pm-6pm. The stored values are
//                                 in store/constants/slots.js. The fifth
//                                 option, 'Today', only renders before 12pm
//                                 London (lib/same-day.js, the hour from
//                                 Order::TimeSlots) and carries
//                                 the 'Extra for same day' surcharge, so it
//                                 is not described as one of the windows.
//   "nothing is charged when      app/models/concerns/user/stripe.rb:
//   you book"                     create_stripe_setup_intent with
//                                 usage: 'off_session', and the Checkout
//                                 session opens in mode: 'setup'. No charge
//                                 is made anywhere in the booking path;
//                                 Order::AcceptService#process_stripe_payment
//                                 is the only place is_charged is set.
//   "held by Stripe, so we        The card lives on the Stripe customer
//   never see the number"         record. All this app keeps is the brand,
//                                 the last four digits and the expiry, which
//                                 is what the FAQ "Where are my card details
//                                 kept?" also says. Say STRIPE, by name, and
//                                 never "PCI": the logo borrows trust a
//                                 claim about our own compliance scope does
//                                 not, and the scope is Stripe's anyway
//                                 (Robin's ruling, 5 Oct).
//
// STEP 2
//   "about half an hour out"      config/whatsapp_templates.yml
//                                 order_on_my_way: "They should be with you
//                                 in about 30 minutes." Same figure in the
//                                 SMS twin, config/locales/en.yml
//                                 sms.customer.on_my_way: "Approximate ETA
//                                 30 minutes." Two independent sources, and
//                                 the one number on the day this business
//                                 already commits to in writing.
//   "when the van is outside"     order_arrived: "your GoWashMyCar washer is
//                                 outside now... Please come out and meet
//                                 them so they can make a start."
//   both messages reach them      OrdersEvents#on_my_way / #on_arrived, and
//                                 docs/WHATSAPP.md "Which channel": WhatsApp
//                                 when reachable, otherwise the SMS. Both
//                                 are guarded on order.web?, which a booking
//                                 made on this page is.
//
// STEP 3
//   "water, power and kit all     app/views/seo/llms.text.erb: "operators
//   on board"                     come to the customer's home, office or
//                                 street in a fully equipped van, carrying
//                                 their own water and power".
//   "no tap and no socket"        seo_helper.rb FAQ, verbatim question: "Do
//                                 you need water or electricity from me?
//                                 No."
//   "snow foam, a hand wash,      Our own photographs of our own jobs, which
//   a microfibre dry"             is the only evidence on this site that
//                                 stock photography cannot fake:
//                                 Order::LiveFeed::SHOWCASE_TILES and its
//                                 mirror sections/hero/frames.js. Frame 05 is
//                                 "a Mercedes SUV covered in snow foam
//                                 outside a brick terrace in Greenwich",
//                                 frame 07 "a car under a full coat of snow
//                                 foam being rinsed on a cobbled street in
//                                 Highbury", frame 10 "a gloved hand buffing
//                                 a car panel dry with a microfibre cloth in
//                                 Wimbledon". The hand wash is also the
//                                 catalogue's own words: Exterior Wash & Wax
//                                 is described "Exterior hand wash with wax
//                                 protection" (services API), and
//                                 "Exterior hand wash" is the first tick on
//                                 three of §7.3's lists.
//                                 SCOPED to an outside wash on purpose.
//                                 Interior Only Valet does not touch the
//                                 paint at all ("The paint is not washed",
//                                 §7.3), so stating foam on every wash would
//                                 be false for one of the six.
//   "photographs when it is       docs/WHATSAPP.md Flows: wash_completed_v2
//   done"                         carries a photo header, then paced
//                                 wash_completion_photo sends for the rest
//                                 (Whatsapp::OrderCompletedFollowupWorker).
//                                 The email twin is order_completed.
//
// THE ANSWERS
//   someone in / interior keys    faqs/index.js, "Do I need to be present?":
//                                 "No! For the exterior wash services, our
//                                 Operators come straight to your car... For
//                                 interior wash services, the Operator will
//                                 contact you to get access to the car."
//                                 Published policy with no code behind it,
//                                 which is why it is worded as what the
//                                 washer does rather than as a guarantee.
//   where the car has to be       faqs/index.js, "Where does my car have to
//                                 be?": "Anywhere that is publicly
//                                 accessible." And "Car location": "When the
//                                 car is located in an underground car park
//                                 please check the location allows car
//                                 washing services to take place."
//                                 NO DISTANCE IS GIVEN HERE, for two
//                                 reasons. §7.6 already carries "About ten
//                                 metres of kerb", and the two numbers in
//                                 the codebase disagree: the FAQ and
//                                 seo_helper say about 10 metres, the
//                                 booking confirmation partial
//                                 (app/views/shared/_booking_notice.html
//                                 .haml) says 20. That is in the report.
//   rain and moving a booking     faqs/index.js, "Bad Weather": we reschedule
//                                 on request. The FAQ says "contact your
//                                 Operator", which has not been possible
//                                 since the operator became internal on
//                                 2026-09-29, so this says contact us.
//                                 NO NOTICE PERIOD AND NO FEE IS STATED. The
//                                 FAQ says free up to 2 hours before the
//                                 window then 50% of the fee; the terms page
//                                 says 24 hours, a 50% refund (the opposite
//                                 meaning) and a 4 hour no-refund cliff the
//                                 FAQ never mentions; Order::CancelService
//                                 applies no time test and no fee at all and
//                                 issues full credit. Three sources, three
//                                 policies. Publishing any one of them would
//                                 be picking a side in a contradiction, so
//                                 this answer states only the part all three
//                                 agree on: a person does it, by hand, when
//                                 you ask. Flagged in the report as a
//                                 blocker.
//   child seats                   faqs/index.js, "Child seats and other
//                                 detachable items": "If you require the
//                                 area under detachable items to be cleaned
//                                 please remove these items prior to the wash
//                                 taking place. Please note the operator will
//                                 not remove or re-attach any of these
//                                 items."
//   price changing on the day     app/views/shared/_booking_notice.html.haml,
//                                 which goes out with every booking
//                                 confirmation email: "GoWashMyCar may adjust
//                                 the price if the car requires additional
//                                 work. Your operator will let you know
//                                 before starting." This is a term every
//                                 customer is already sent and which appears
//                                 nowhere on the home page. It belongs with
//                                 the questions rather than with the prices:
//                                 it is not what a wash costs, it is what
//                                 happens when the washer is standing at the
//                                 car.
//
// =====================================================================
// WHAT IS DELIBERATELY NOT HERE
// =====================================================================
//
// No time to acceptance, no duration for the wash, no notice period, no
// cancellation fee, no distance in metres: all five are above, each with the
// reason.
//
// No second telling of when the card is charged. Step 1 says nothing is
// charged when you book and that Stripe holds the card, the fold says the
// first half of that, and that is enough: a page that keeps returning to
// when the money moves reads as a page with something to explain away.
//
// No operator's name, no insurance, no rating, no vetting: that is §7.6,
// three screens down, and it is the one section allowed to talk about the
// person. No tick lists and no prices: §7.3. No photographs: §7.4 is a whole
// section of them and the hero leads on five.
//
// No CTA. §7.2 does not ask for one, the fold is a scroll above and §7.7 is
// the page's one closing button. The two links at the foot of the answers are
// a way to ask a question, not a way to buy.
//
// WHAT BELONGS ON /faqs AND NOT HERE. The FAQ page carries seventeen
// questions in three groups. What is here is only what stops a first
// booking: being in, where the car goes, weather, child seats, the price on
// the day. What stays there is everything that matters after you have
// decided: receipts for expenses, bespoke detailing quotes, the waiting-time
// charge, hours of operation, which boroughs are covered. One text link
// carries a reader from here to there.
//
// That page was rebuilt on 2026-10-05 and the note that used to sit here is
// settled: its "When is my payment processed?" answer no longer promises a
// confirmation email that stopped going out on 2026-09-29, and it no longer
// states a cancellation notice period or a distance in metres, for the same
// reason this section does not. The three contradictions behind that are
// written up at the top of pages/faqs/questions/faqs.js and are Robin's to
// settle.
//
// =====================================================================
// SHAPE AND MARKUP
// =====================================================================
//
// The moments are an <ol>, because the order is the content and the element
// says so to a screen reader and a crawler without the painted numerals. The
// numerals are therefore decorative duplication and are aria-hidden
// (DESIGN.md §7). Each one sits in its own left column with the title
// baseline-aligned beside it, so three accent serif digits line up down a
// left edge and read as a spine. That is the whole visual, and it is why there is
// no rule, no arrow, no glyph and no card: there is no icon set on this site
// and the numerals do not need help.
//
// The questions are a <dl>, because a question and its answer is the one
// thing a definition list is actually for. §7.6 is also a stone band and also
// uses a <dl>, so this one is deliberately plainer: no hairline rules, the
// question in body size rather than label size, and a question mark on every
// one. A reader meeting this three screens before §7.6 should not feel they
// have seen the same table twice.
//
// Heading levels: H2 from <SectionHeading>, then H3 on each moment and one H3
// over the questions. Nothing is skipped and the section still has one H2.
//
// MEASURED, not estimated, in Chrome against the real tokens.css and
// primitives.css at three widths:
//
//   1440  section 944px tall. The two columns come out at 698px each, which
//         is a coincidence worth keeping: the copy was cut to length and the
//         columns balanced themselves. Body measure 542px, answers 506px,
//         both inside --container-read.
//    760  section 1,378px. One column, both blocks capped at the measure.
//    390  section 1,782px, document scrollWidth 390 (no horizontal scroll),
//         both links 44px tall.
//
// 1,782px AT 390 IS THE ONE NUMBER TO ARGUE WITH. The page target is 4,200px
// at that width (DESIGN.md §6) and this is the third of eight sections, so
// this band is a bigger share of a phone page than any single section should
// be. It is 1,100px more than the three-step version it replaces, and the
// extra buys six more answered objections on the section that sits between
// the prices and the proof. I think that is the right trade on a page where
// 88.5% of the people who reach the booking form never pay, but it is a
// page-level call and I can only see one section. If the budget bites, the
// cheapest cut is the whole `.hiw-asks` block: delete ANSWERS and the <div>,
// and the band drops to about 900px without touching the three moments.
//
// The WhatsApp link takes whatsappChatProps, which keeps the wa.me href for a
// phone and opens the number form on a desktop instead (docs/WHATSAPP.md,
// "Chat on WhatsApp on a desktop"): a bare wa.me link on a laptop is the dead
// end that fix exists for. The header and footer already import it, so it
// costs the home chunk nothing. §7.6 owns the telephone number and this owns
// the message, which keeps one contact route per section rather than two of
// each.

// Every line below is as short as the fact allows, and that is a budget
// rather than a style. The first draft of this copy measured 1,854px at 390;
// cutting it to this measured 1,782px and, at 1440, balanced the two columns
// to the pixel. Nothing was dropped to get there, only words.
//
// THE 23 OCT 2026 WORDING (source: Barry's suggested scripts, edited by the
// PM; Robin, 23 Oct: "the new language should just be reflected in all
// versions"). Steps 2 and 3 and all four answers are now the same words
// as /faqs (pages/faqs/questions/faqs.js), SeoHelper::FAQ, the wash pages
// (seo/wash_pages.json), the booking confirmation's notice
// (app/views/shared/_booking_notice.html.haml) and the coach's playbook and
// policies: change one, change them all. What changed against the notes
// above:
//   - step 2 says "we message you when they set off and again when they
//     arrive" (order_on_my_way / order_arrived, WhatsApp or SMS), not "text";
//   - step 3 puts the snow foam first "where the spot allows" (Barry's
//     script scopes it: not every spot permits it) and offers wax or
//     sealant as an add-on for extra shine and protection;
//   - the interior answer names the ways in (a key with a neighbour or
//     concierge, or the car's app) instead of "we'll sort access with you";
//   - the car answer adds "safe and legal" and "space to open the doors",
//     and still gives no distance in metres;
//   - rain: "plenty of customers go ahead", and the move is asked for before
//     the valeter sets off. Still no notice period and no fee;
//   - the price answer now carries the on-the-day rule the booking notice
//     always had: anything extra is agreed with you first.
// 25 Oct 2026 (Barry, via Robin): no water type and no pH on any customer
// page, so step 3 says "a thick snow foam" and "a rinse".
// The column measurements above predate this copy; it is longer.
// 26 Oct 2026 (Robin: use Barry's scripts "fairly closely"): the three
// steps, the add-on line and the Book button are Barry's How it works
// (WhatsApp, 23 Oct), changed only where the house rules say: "message"
// for his "text message" (ours go by WhatsApp), "they", British spelling.
// Step 3 is the same words as the wash pages' process intro
// (seo/wash_pages.json `process`). Still no water type and no pH.
// EVERYTHING ABOVE ABOUT STEP 3 IS NOW HISTORY: 4 Nov 2026 took the wash out
// of it altogether. See the note on step 3 itself.
const STEPS = [
  {
    title: 'You pick the valet your car needs',
    body:
      'Every valet is listed in full with its price, so you can see exactly what is done before you ' +
      'book. Tell us anything we should know about the car in the notes and your valeter will read it.'
  },
  {
    title: 'We tell you when we are on the way',
    body: 'Your valeter messages you when they set off, and again when they arrive at the car.'
  },
  // 4 NOVEMBER 2026, BARRY VIA ROBIN: "Barry requested we dont do deep into
  // our process, snow foam this and that, we should have the pictures as you
  // do with suds all over the car, but lets not embellish the type of wash
  // etc..". Step 3 was his own 23 Oct script, which walked the customer
  // through the wash. It is the same length and in the same place, and the
  // photographs elsewhere on the page are untouched, but it now says what
  // the customer ends up with rather than what we do to the paint. No
  // product, stage, technique or kit is named. Matches the wash pages'
  // shared block (seo/wash_pages.json `process`): change one, change both.
  {
    title: 'Your car is cleaned, and you get the photographs',
    body:
      "Your valeter cleans the car where it is parked, with the van's own water and power, and nothing " +
      'needed from your house. They photograph it when it is finished, so you can see the job whether ' +
      'or not you were there.'
  }
]

const ADD_ON =
  'A protective coating can be added to any valet. It deepens the shine, holds the freshly cleaned ' +
  'look for longer and puts a layer between your paint and the London weather.'

// The four answers are seo/good_to_know.json (25 Oct 2026), the one copy
// /faqs, SeoHelper::FAQ and every wash page's Good to know block read too.
// Plain text here: this section keeps one contact route, the WhatsApp link
// below, so the answers carry no links of their own.
// File order (price, where, rain); be home is hidden on 'home' (25 Oct 2026).
const ANSWERS = goodToKnowFor('home')

// The section ends on the booking, like every other one (copy deck, 5 Oct).
// The figure is the server's (lib/page-prices.js), never typed.
const BookButton = () => {
  const from = readLowestWashPrice()
  return (
    <Button
      className="hiw-book-btn"
      variant="primary"
      href={bookingPath(defaultWashSlug())}
      onClick={event => {
        event.preventDefault()
        enterBooking(defaultWashSlug(), 'how_it_works')
      }}>
      {from ? `Book your wash, from ${from}` : 'Book your wash'}
    </Button>
  )
}

export const HowItWorks = () => (
  <Section className="hiw" id="how-it-works" background="stone">
    <SectionHeading intro="No tap, no socket, no need to be home.">How we work</SectionHeading>

    <div className="hiw-grid">
      <div className="hiw-main">
        <ol className="hiw-steps">
          {STEPS.map((step, i) => (
            <li className="hiw-step" key={step.title}>
              <span className="hiw-step-number" aria-hidden="true">
                {i + 1}
              </span>
              <h3 className="gwmc-h3 hiw-step-title">{step.title}</h3>
              <p className="hiw-step-body">{step.body}</p>
            </li>
          ))}
        </ol>

        <p className="hiw-add-on">{ADD_ON}</p>

        <div className="hiw-book">
          <BookButton />
        </div>
      </div>

      <div className="hiw-asks">
        <h3 className="gwmc-h3 hiw-asks-title">Quick answers</h3>

        <dl className="hiw-qa">
          {ANSWERS.map(item => (
            <div className="hiw-qa-row" key={item.q}>
              <dt className="hiw-q">{item.q}</dt>
              <dd className="hiw-a">{item.a}</dd>
            </div>
          ))}
        </dl>

        <p className="hiw-help">Ask us anything before you book, and one of us will answer.</p>
        <a
          className="gwmc-link hiw-help-link"
          {...whatsappChatProps('Hi GoWashMyCar! I have a question about booking a wash')}>
          Message us on WhatsApp
        </a>
        <a className="gwmc-link hiw-help-link" href="/faqs">
          All FAQs
        </a>
      </div>
    </div>
  </Section>
)
